import React, { useState, useEffect } from 'react';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import moment from 'moment';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

interface SafeGridProps {
  data: any[];
  loading: boolean;
  onEdit: (row: any) => void;
  onDelete: (row: any) => void;
  onSort: (sortingInfo: { sortBy: string; sortDir: string }) => void;
  sortingInfo: { sortBy: string; sortDir: string };
}

export default function SafeCredsGrid({
  data,
  loading,
  onEdit,
  onDelete,
  onSort,
  sortingInfo,
}: SafeGridProps) {
  const [localData, setLocalData] = useState(data);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Sync with API changes
  useEffect(() => {
    setLocalData(data);
  }, [data]);

  // PAGINATION SLICE
  const start = (page - 1) * pageSize;
  const paginatedData = localData.slice(start, start + pageSize);

  const totalPages = Math.ceil(localData.length / pageSize);

  // PAGE CHANGE
  const handlePageChange = (_event: any, newPage: number) => {
    setPage(newPage);
  };

  const handlepageSizeChange = (event: any) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setPage(1);
  };

  const columns = [
    { key: 'type', name: 'TYPE', width: 180 },
    { key: 'name', name: 'NAME', width: 250 },
    {
      key: 'owner',
      name: 'OWNER',
      width: 220,
      formatter: ({ row }: any) => <div>{row.owner}</div>,
    },
    { key: 'environment', name: 'ENVIRONMENT', width: 150 },
    {
      key: 'creationDate',
      name: 'CREATION DATE',
      width: 160,
      formatter: ({ row }: any) =>
        row.creationDate ? moment.utc(row.creationDate).format('MM-DD-YYYY') : '-',
    },
    {
      key: 'lastRenewalDate',
      name: 'LAST RENEWAL',
      width: 160,
      formatter: ({ row }: any) =>
        row.lastRenewalDate ? moment.utc(row.lastRenewalDate).format('MM-DD-YYYY') : '-',
    },
    {
      key: 'expiryDate',
      name: 'EXPIRY DATE',
      width: 150,
      formatter: ({ row }: any) =>
        row.expiryDate ? moment.utc(row.expiryDate).format('MM-DD-YYYY') : '-',
    },
    {
      key: 'renewalContact',
      name: 'RENEWAL CONTACT',
      width: 220,
      formatter: ({ row }: any) => <div>{row.renewalContact?.[0]}</div>,
    },
    {
      key: 'actions',
      name: 'ACTIONS',
      width: 120,
      formatter: ({ row }: any) => (
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <EditIcon
            style={{ cursor: 'pointer', color: '#1E88E5', fontSize: '20px' }}
            onClick={() => onEdit(row)}
          />
          <DeleteIcon
            style={{ cursor: 'pointer', color: '#D32F2F', fontSize: '20px' }}
            onClick={() => onDelete(row)}
          />
        </div>
      ),
    },
  ];

  // -----------------------------
  // SORTING IMPLEMENTATION
  // -----------------------------
  const handleSorting = (sortingInfo: any) => {
    const { sortBy, sortDir } = sortingInfo;

    setPage(1);
    onSort({ sortBy, sortDir });
  };

  return (
    <CustomizedDataGrid
      tableColumns={columns}
      tableData={paginatedData}
      tableType="CredList"
      defaultColumnNames={columns.map((c) => c.name)}
      // SORT SUPPORT
      onSorting={handleSorting}
      currSorting={sortingInfo}
      // Required props
      currFilter={{}}
      filterDataOptions={{}}
      searchDataValues={{}}
      onFilterCheck={() => {}}
      onFilterSearch={() => {}}
      onHeaderSelectAll={() => {}}
      onFilterClick={() => {}}
      exportTableData={() => {}}
      // Pagination
      page={page}
      pageSize={pageSize}
      listLength={localData.length}
      paginationData={{
        totalElements: localData.length,
        totalPages,
      }}
      handlePageChange={handlePageChange}
      handlepageSizeChange={handlepageSizeChange}
      isFetching={loading}
      isDisabled={false}
      isShowMoreButton={false}
      isDateRangeFilter={false}
    />
  );
}
